// src/pages/RoomCreatePage.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Chip,
  LinearProgress,
  Divider
} from "@mui/material";
import { useSnackbar } from "notistack";
import { createRoom, getAllRooms, uploadRoomImage } from "../services/roomService";
import { getRoomFields } from "../services/roomFieldService";
import { getSubscription } from "../services/paymentService";

// Simple file validation utilities - no Image object creation
const validateImageFile = (file) => {
  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const minFileSize = 100; // 100 bytes
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  
  if (file.size > maxFileSize) {
    throw new Error(`File size exceeds 10MB limit. Current: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
  }
  
  if (file.size < minFileSize) {
    throw new Error('File too small - minimum 100 bytes required');
  }
  
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Invalid file type. Expected JPG, PNG, or WebP, got: ${file.type}`);
  }
  
  // Return immediately - no async validation
  return true;
};

// Custom hook for file upload management
const useFileUpload = (onError) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = useCallback((files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    try {
      validateImageFile(file);
      setSelectedFile(file);
      setError(null);
    } catch (error) {
      setSelectedFile(null);
      setError(error.message);
      onError?.(error.message);
    }
  }, [onError]);

  const clearFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
  }, []);

  return {
    selectedFile,
    error,
    handleFileSelect,
    clearFile
  };
};

export default function RoomCreatePage() {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [capacity, setCapacity] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [roomFields, setRoomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [currentRoomCount, setCurrentRoomCount] = useState(0);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [createdRoom, setCreatedRoom] = useState(null);
  const [roomImages, setRoomImages] = useState([]);
  
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // Get user and organization from Redux store
  const user = useSelector((state) => state.user.user);
  const organization = user?.organization;

  // Use the custom file upload hook
  const { selectedFile, error, handleFileSelect, clearFile } = useFileUpload((errorMessage) => {
    enqueueSnackbar(errorMessage, { variant: 'error' });
  });

  const loadRoomFields = useCallback(async () => {
    try {
      setFieldsLoading(true);
      const fields = await getRoomFields();
      setRoomFields(fields || []);
      
      // Initialize custom field values with defaults
      const initialValues = {};
      fields?.forEach(field => {
        if (!field.isSystem && field.defaultValue !== undefined) {
          initialValues[field.name] = field.defaultValue;
        }
      });
      setCustomFieldValues(initialValues);
    } catch (error) {
      console.error('Error loading room fields:', error);
      enqueueSnackbar('Failed to load room fields', { variant: 'error' });
    } finally {
      setFieldsLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadSubscriptionData = useCallback(async () => {
    if (!organization?._id) return;
    
    try {
      setSubscriptionLoading(true);
      
      // Fetch subscription and current room count in parallel
      const [subscriptionData, roomsData] = await Promise.all([
        getSubscription(organization._id),
        getAllRooms()
      ]);
      
      setSubscription(subscriptionData.subscription);
      setCurrentRoomCount(roomsData.length);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      // Don't show error message since subscription may not exist yet
    } finally {
      setSubscriptionLoading(false);
    }
  }, [organization?._id]);

  useEffect(() => {
    // Only load fields if user is authenticated
    if (user) {
      loadRoomFields();
      loadSubscriptionData();
    }
  }, [user, loadRoomFields, loadSubscriptionData]);

  // Note: Removed window focus event listener that was causing form to reset
  // The focus listener was triggering loadRoomFields() when file dialogs opened/closed,
  // which called setCustomFieldValues(initialValues) and wiped out all user input

  const handleCustomFieldChange = (fieldName, value) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleImageUpdate = (newImage) => {
    if (createdRoom) {
      setRoomImages(prev => [...prev, newImage]);
    }
    // Note: selectedFile is now managed by the useFileUpload hook
  };

  const handleImageRemove = useCallback(() => {
    clearFile();
  }, [clearFile]);

  // Handle file input click in parent component
  const handleFileInputClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Handle file input change in parent component  
  const handleFileInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    e.target.value = '';
  }, [handleFileSelect]);

  // Success notification for image selection
  useEffect(() => {
    if (selectedFile && !error) {
      enqueueSnackbar('Image selected successfully', { variant: 'success' });
    }
  }, [selectedFile, error, enqueueSnackbar]);

  const renderCustomField = (field) => {
    const value = customFieldValues[field.name] || '';
    const helperText = field.description || (field.required ? 'Required' : 'Optional');
    
    switch (field.type) {
      case 'text':
        return (
          <TextField
            key={field._id}
            label={field.label}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            required={field.required}
            fullWidth
            helperText={helperText}
          />
        );
      
      case 'number':
        return (
          <TextField
            key={field._id}
            label={field.label}
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            required={field.required}
            fullWidth
            helperText={helperText}
          />
        );
      
      case 'select':
        return (
          <FormControl key={field._id} fullWidth required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
              label={field.label}
            >
              <MenuItem value="">
                <em>Select {field.label}</em>
              </MenuItem>
              {field.options?.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {helperText}
              </Typography>
            )}
          </FormControl>
        );
      
      case 'boolean':
        return (
          <Box key={field._id}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true || value === 'true'}
                  onChange={(e) => handleCustomFieldChange(field.name, e.target.checked)}
                />
              }
              label={field.label}
            />
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                {helperText}
              </Typography>
            )}
          </Box>
        );
      
      default:
        return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚀 Form submitted with:', { name, capacity, location, customFieldValues });
    console.log('🔍 Current state:', {
      organization: !!organization,
      organizationId: organization?._id
    });
    
    if (!name.trim()) {
      enqueueSnackbar("Room name is required", { variant: "error" });
      return;
    }

    // Validate required custom fields
    const requiredFields = roomFields.filter(field => field.required && !field.isSystem);
    for (const field of requiredFields) {
      const value = customFieldValues[field.name];
      if (!value || (typeof value === 'string' && !value.trim())) {
        enqueueSnackbar(`${field.label} is required`, { variant: "error" });
        return;
      }
    }

    // Store form data for later use
    const formData = { 
      name: name.trim(), 
      capacity: capacity ? parseInt(capacity) : undefined, 
      location: location.trim() || undefined,
      customFields: customFieldValues
    };

    // If user has an organization, create room directly
    if (organization) {
      console.log('⚡ Creating room directly - organization found');
      await createRoomAndImages(formData);
    } else {
      console.log('⚠️ No organization found for user, skipping room creation');
      enqueueSnackbar("No organization found for user, room creation skipped", { variant: "warning" });
    }
  };

  const createRoomAndImages = async (formData) => {
    try {
      setLoading(true);
      console.log('🏗️ Creating room directly:', formData);
      
      let roomResponse = null;
      
      try {
        // Create the room first
        roomResponse = await createRoom(formData);
        console.log('🏗️ Room creation response:', roomResponse);
        
        if (roomResponse.billingUpdateCompleted) {
          // Billing update was completed for the new room
        } else {
          // Note: billing update was not completed
        }
        
        setCreatedRoom(roomResponse);
        
        // Upload selected images if any
        if (selectedFile) {
          enqueueSnackbar('Room created successfully! Uploading image...', { variant: "info" });
          
          // If image upload fails, we need to delete the created room
          try {
            const uploadResponse = await uploadRoomImage(roomResponse._id, selectedFile, 'gallery');
            setRoomImages([uploadResponse.image]);
          } catch (imageError) {
            console.error('Image upload failed:', imageError);
            
            // Delete the room since image upload failed
            try {
              const { deleteRoom } = await import("../services/roomService");
              await deleteRoom(roomResponse._id);
            } catch (deleteError) {
              console.error('Failed to delete room after image upload failure:', deleteError);
            }
            
            // Throw the original image error
            throw new Error(`Failed to upload image: ${imageError.response?.data?.error || imageError.message}`);
          }
        }
        
        enqueueSnackbar(
          selectedFile 
            ? 'Room created successfully with image!'
            : "Room created successfully!",
          { variant: "success" }
        );
        
        // Navigate to rooms page after a short delay
        setTimeout(() => {
          navigate("/rooms");
        }, 1500);
        
        // Refresh subscription data to update room usage display
        await loadSubscriptionData();
        
      } catch (roomOrImageError) {
        // If this is an image upload error after room creation, the error is already handled above
        // If this is a room creation error, throw it normally
        throw roomOrImageError;
      }
      
    } catch (err) {
      console.error("Room creation error:", err);
      
      // Handle room limit reached error
      if (err.response?.data?.code === 'ROOM_LIMIT_REACHED') {
        const errorData = err.response.data;
        enqueueSnackbar(
          `Room limit reached! You have ${errorData.currentRooms}/${errorData.maxRooms} rooms. Please upgrade your subscription to add more rooms.`,
          { variant: "error", autoHideDuration: 8000 }
        );
      } else {
        enqueueSnackbar(
          err.response?.data?.error || err.message || "Failed to create room",
          { variant: "error" }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user || fieldsLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
              Create a Room
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <Typography>{!user ? 'Loading...' : 'Loading room fields...'}</Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom textAlign="center">
            Create a Room
          </Typography>

          {/* Room Usage Display */}
          {subscription && !subscriptionLoading && (
            <Box sx={{ mb: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Room Usage
                    </Typography>
                    <Chip 
                      label={`${currentRoomCount}/${subscription.maxRooms} rooms`}
                      color={currentRoomCount >= subscription.maxRooms ? "error" : "primary"}
                      variant="outlined"
                    />
                  </Box>

                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                      {currentRoomCount} of {subscription.maxRooms} rooms used
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((currentRoomCount / subscription.maxRooms) * 100, 100)}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                      color={currentRoomCount >= subscription.maxRooms ? "error" : "primary"}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {Math.round((currentRoomCount / subscription.maxRooms) * 100)}%
                    </Typography>
                  </Box>

                  {currentRoomCount >= subscription.maxRooms ? (
                    <Alert severity="warning">
                      You've reached your room limit. 
                      <Button 
                        component={Link} 
                        to="/org-settings#billing" 
                        size="small" 
                        sx={{ ml: 1 }}
                      >
                        Upgrade Plan
                      </Button>
                    </Alert>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      You can create {subscription.maxRooms - currentRoomCount} more room{subscription.maxRooms - currentRoomCount !== 1 ? 's' : ''}.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <TextField
              label="Room Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />

            {/* All Fields in Order */}
            {roomFields
              .sort((a, b) => a.order - b.order)
              .map(field => {
                const helperText = field.description || (field.required ? 'Required' : 'Optional');
                
                // Handle system fields
                if (field.isSystem) {
                  if (field.name === 'location') {
                    return (
                      <TextField
                        key={field._id}
                        label={field.label}
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        required={field.required}
                        fullWidth
                        helperText={helperText}
                      />
                    );
                  }
                  if (field.name === 'capacity') {
                    return (
                      <TextField
                        key={field._id}
                        label={field.label}
                        type="number"
                        value={capacity}
                        onChange={(e) => setCapacity(e.target.value)}
                        required={field.required}
                        fullWidth
                        helperText={helperText}
                      />
                    );
                  }
                  return null;
                }
                
                // Handle custom fields
                return renderCustomField(field);
              })}

            {/* Room Images Section */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Room Image
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add a photo to showcase your room (optional)
              </Typography>
              
              {/* Hidden file input in parent component */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
                disabled={loading}
              />
              
              <SimpleImageUploader
                selectedImage={selectedFile}
                onImageSelect={handleFileSelect}
                onImageRemove={handleImageRemove}
                onFileInputClick={handleFileInputClick}
                disabled={loading}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              fullWidth
              sx={{ mt: 3 }}
            >
              {loading ? (
                <>
                  {selectedFile ? 'Creating Room & Uploading Image...' : 'Creating Room...'}
                </>
              ) : (
                "Create Room"
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

// Simplified image uploader component - file input is now in parent
const SimpleImageUploader = React.memo(({ selectedImage, onImageSelect, onImageRemove, onFileInputClick, disabled }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragActive false if we're actually leaving the drop area
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Prevent default to allow drop
    return false;
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onImageSelect(files);
    }
  }, [disabled, onImageSelect]);

  // File input handling moved to parent component

  // Memoize image preview URL to prevent recreation on every render
  const imagePreviewUrl = useMemo(() => {
    return selectedImage ? URL.createObjectURL(selectedImage) : null;
  }, [selectedImage]);

  // Cleanup URL when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  return (
    <Box sx={{ position: 'relative' }}>

      {/* Selected Image Preview */}
      {selectedImage && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Selected Image
          </Typography>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 400,
              height: 300,
              border: '1px solid #ddd',
              borderRadius: 2,
              overflow: 'hidden',
              opacity: 1,
              transition: 'opacity 0.3s ease',
              backgroundColor: '#f9f9f9'
            }}
          >
            <img
              src={imagePreviewUrl}
              alt="Selected room image"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                imageRendering: 'high-quality',
                filter: 'contrast(1.05) saturate(1.1)',
                transition: 'transform 0.2s ease'
              }}
              onLoad={(e) => {
                // Ensure image is crisp
                e.target.style.imageRendering = 'auto';
              }}
            />
            <Button
                size="small"
                onClick={onImageRemove}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  minWidth: 'auto',
                  width: 32,
                  height: 32,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  color: 'error.main',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    transform: 'scale(1.05)'
                  }
                }}
              >
                ×
              </Button>
            {/* Image info overlay */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                padding: '4px 8px',
                fontSize: '0.75rem'
              }}
            >
              {selectedImage.name} • {(selectedImage.size / (1024 * 1024)).toFixed(2)}MB
            </Box>
          </Box>
        </Box>
      )}

      {/* Upload Area */}
      {!selectedImage && (
        <Box
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            padding: 3,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
            borderColor: dragActive ? 'primary.main' : '#ccc',
            opacity: disabled ? 0.5 : 1,
            '&:hover': !disabled ? {
              borderColor: 'primary.main',
              backgroundColor: 'rgba(25, 118, 210, 0.02)'
            } : {}
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => {
            if (!disabled && onFileInputClick) {
              onFileInputClick();
            }
          }}
        >
          <Typography variant="body1" sx={{ mb: 1 }}>
            {dragActive ? 'Drop your image here' : 'Drag & drop an image here, or click to select'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload an image (JPG, PNG, WebP)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Max size: 10MB • Max dimensions: 5000x5000px
          </Typography>
        </Box>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  // Simple comparison that doesn't block file selection
  return (
    prevProps.selectedImage === nextProps.selectedImage &&
    prevProps.disabled === nextProps.disabled
    // Note: Not comparing function props to prevent blocking
  );
});

SimpleImageUploader.displayName = 'SimpleImageUploader';